import { ExperimentType } from "@prismancy/shared"
import { ChevronDown, FlaskConical } from "lucide-react"
import React, { useEffect, useId, useRef, useState } from "react"
import ContextMenuAction from "../../elements/ContextMenuAction"
import IconFont from "../../elements/IconFont"
import useOutsideClick from "../../hooks/useOutsideClick"

type CreateExperimentMenuProps = {
  isCreating?: boolean
  triggerVariant?: "button" | "link"
  showRedirectTest?: boolean
  onSelect: (type: ExperimentType) => void
}

const CreateExperimentMenu: React.FC<CreateExperimentMenuProps> = ({
  isCreating = false,
  triggerVariant = "button",
  showRedirectTest = false,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const menuId = useId()
  const firstItemRef = useRef<HTMLDivElement>(null)
  const outsideClickRef = useOutsideClick(() => setIsOpen(false)) as React.RefObject<HTMLDivElement>

  useEffect(() => {
    if (!isOpen) return

    const frameId = window.requestAnimationFrame(() => {
      firstItemRef.current?.querySelector<HTMLButtonElement>('button')?.focus()
    })

    return () => window.cancelAnimationFrame(frameId)
  }, [isOpen])

  useEffect(() => {
    if (isCreating) {
      setIsOpen(false)
    }
  }, [isCreating])

  const handleSelect = (type: ExperimentType) => {
    if (isCreating) return
    setIsOpen(false)
    onSelect(type)
  }

  const triggerClassName = triggerVariant === "button"
    ? "btn-secondary btn-sm w-auto px-[16px] gap-[4px]"
    : "link-action mt-[18px] gap-[4px] font-semibold"

  return (
    <div ref={outsideClickRef} className="relative inline-flex">
      <button
        type="button"
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-controls={menuId}
        aria-busy={isCreating}
        disabled={isCreating}
        onClick={() => setIsOpen((current) => !current)}
      >
        {triggerVariant === "link" && (
          <IconFont name="plus" size={16} className="text-inherit" />
        )}
        <span>Create Experiment</span>
        <ChevronDown
          size={16}
          className={`h-4.5 w-4.5 text-inherit transition-transform ${isOpen ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {isOpen && (
        <div
          id={menuId}
          role="menu"
          className={`ui-layer-popover absolute top-[calc(100%+4px)] z-50 min-w-[160px] rounded-[6px] border border-border-default bg-background-surface-02 p-[16px] shadow-[0_4px_20px_0_var(--ui-shadow-xl)] ${
            triggerVariant === "button"
              ? "right-0"
              : "left-1/2 -translate-x-1/2"
          }`}
        >
          <div className="flex flex-col gap-[8px]">
            <div ref={firstItemRef}>
              <ContextMenuAction
                className="ui-create-experiment-action group items-center"
                disabled={isCreating}
                onClick={() => handleSelect(ExperimentType.AB)}
              >
                <span>A/B Test</span>
                <FlaskConical aria-hidden="true" className="h-5 w-5 shrink-0 text-current transition-colors" strokeWidth={1.25} />
              </ContextMenuAction>
            </div>
            {showRedirectTest && (
              <div>
                <ContextMenuAction
                  className="ui-create-experiment-action group items-center"
                  disabled={isCreating}
                  onClick={() => handleSelect(ExperimentType.REDIRECT)}
                >
                  <span>Redirect Test</span>
                  <RedirectTestIcon aria-hidden="true" className="h-5 w-5 shrink-0 text-current transition-colors" />
                </ContextMenuAction>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

const RedirectTestIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    width="10"
    height="9"
    viewBox="0 0 10 9"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
  >
    <path
      d="M1.39149 8.00003C1.97446 8.00003 2.44705 7.52744 2.44705 6.94447C2.44705 6.3615 1.97446 5.88892 1.39149 5.88892C0.808526 5.88892 0.335938 6.3615 0.335938 6.94447C0.335938 7.52744 0.808526 8.00003 1.39149 8.00003Z"
      fill="currentColor"
    />
    <path
      d="M2.55469 6.94442H6.38802C7.12876 6.94442 7.49913 6.57405 7.49913 5.83331V1.11108"
      stroke="currentColor"
      strokeWidth={0.833333}
      strokeLinecap="round"
    />
    <path
      d="M5.83203 2.50007L7.4987 0.72229L9.16536 2.50007"
      stroke="currentColor"
      strokeWidth={0.833333}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

export default CreateExperimentMenu
